import { supabase } from '@/integrations/supabase/client';
import type {
  RealtimeChannel,
  RealtimePostgresInsertPayload,
  RealtimePostgresUpdatePayload,
} from '@supabase/realtime-js';
import type { Database } from '@/integrations/supabase/types';

export type { RealtimePostgresInsertPayload, RealtimePostgresUpdatePayload };

export type OSRow = Database['public']['Tables']['ordens_servico']['Row'];

export interface OSChangeHandlers {
  onInsert: (payload: RealtimePostgresInsertPayload<OSRow>) => void;
  onUpdate: (payload: RealtimePostgresUpdatePayload<OSRow>) => void;
}

/**
 * Subscribes to INSERT + UPDATE events on the global `ordens_servico` table.
 * Returns the channel so the caller can pass it to `unsubscribeChannel` on cleanup.
 * Emits a DEV console log when the subscription becomes active.
 */
export function subscribeToOSChanges(
  handlers: OSChangeHandlers,
  channelName = 'os-disponiveis-realtime',
): RealtimeChannel {
  return supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'ordens_servico' },
      handlers.onInsert,
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'ordens_servico' },
      handlers.onUpdate,
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED' && import.meta.env.DEV) {
        console.log('🔴 Realtime: Escutando mudanças em ordens_servico');
      }
    });
}

/**
 * Subscribes to UPDATE events on `ordens_servico` filtered by a given responsavel_id.
 * Used by admins/atendentes to react to OS being accepted by a prestador in their scope.
 * Returns the channel so the caller can pass it to `unsubscribeChannel` on cleanup.
 */
export function subscribeToOSAceitasFor(
  responsavelId: string,
  onUpdate: (payload: RealtimePostgresUpdatePayload<OSRow>) => void,
): RealtimeChannel {
  return supabase
    .channel(`os-aceitas-${responsavelId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'ordens_servico',
        filter: `responsavel_id=eq.${responsavelId}`,
      },
      onUpdate,
    )
    .subscribe();
}

/**
 * Tears down a realtime channel. Safe to call in a `useEffect` cleanup.
 */
export function unsubscribeChannel(channel: RealtimeChannel): void {
  supabase.removeChannel(channel);
}
